import axios from "axios";
import { baseApi, prepareHeaders } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import {
  CreateSubjectDocumentRequest,
  PaginateQuery,
  SubjectDocumentDto,
  UpdateSubjectDocumentRequest,
} from "../../interfaces";
import { MediaDto } from "../../interfaces/media";
import { Paginated } from "../../models";

const baseUrl = process.env.REACT_APP_API_URL || "/api";

export const subjectDocumentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubjectDocuments: builder.query<
      Paginated<SubjectDocumentDto>,
      PaginateQuery
    >({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/subject-document"),
      }),
      providesTags: ["Paginated-Subject-Documents"],
    }),
    getSubjectDocument: builder.query<SubjectDocumentDto, string>({
      query: (id) => ({ url: `/api/subject-document/${id}` }),
      providesTags: ["SubjectDocument"],
    }),
    addSubjectDocument: builder.mutation<any, CreateSubjectDocumentRequest>({
      query: (data) => ({
        url: "/api/subject-document",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Paginated-Subject-Documents"],
    }),
    updateSubjectDocument: builder.mutation<
      any,
      { id: string; data: UpdateSubjectDocumentRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/subject-document/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["SubjectDocument", "Paginated-Subject-Documents"],
    }),
    deleteSubjectDocument: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/subject-document/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Paginated-Subject-Documents"],
    }),
    toggleSubjectDocument: builder.mutation<any, string>({
      query: (documentId) => ({
        url: `/api/subject-document/toggle-state/${documentId}`,
        method: "PATCH",
        body: {},
      }),
      invalidatesTags: ["SubjectDocument", "Paginated-Subject-Documents"],
    }),
    uploadSubjectDocument: builder.mutation<
      MediaDto,
      {
        file: File;
        subjectDocumentId?: string | null;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, subjectDocumentId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: subjectDocumentId
            ? `/api/subject-document/upload?id=${subjectDocumentId}`
            : "/api/subject-document/upload",
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: ["SubjectDocument"],
    }),
    downloadSubjectDocument: builder.mutation<Blob, string>({
      query: (documentId) => ({
        url: `/api/subject-document/download/${documentId}`,
        responseType: "blob",
      }),
    }),
  }),
});

export const {
  useGetSubjectDocumentsQuery,
  useGetSubjectDocumentQuery,
  useAddSubjectDocumentMutation,
  useUpdateSubjectDocumentMutation,
  useDeleteSubjectDocumentMutation,
  useToggleSubjectDocumentMutation,
  useUploadSubjectDocumentMutation,
  useDownloadSubjectDocumentMutation,
} = subjectDocumentApi;
